import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, MapPin, Phone, Mail, ArrowRight, Sparkles } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="relative bg-gradient-to-b from-neutral-900 via-neutral-900 to-black text-white mt-16 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#FF4D00]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#FF4D00]/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      {/* Top Border Gradient */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#FF4D00] to-transparent"></div>
      
      {/* Newsletter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-b border-white/10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold mb-2">Become a <span className="text-[#FF4D00]">B2B Partner</span></h3>
            <p className="text-neutral-400">Get exclusive wholesale pricing and updates on new products</p>
          </div>
          <Link to="/become-distributor" className="group flex items-center gap-3 bg-gradient-to-r from-[#FF4D00] to-orange-500 hover:from-[#e64500] hover:to-orange-600 text-white px-8 py-4 rounded-full font-bold shadow-lg shadow-[#FF4D00]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#FF4D00]/40">
            <Sparkles className="w-5 h-5" />
            Partner With Us
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <img 
              src="/images/site/site_44.jpg" 
              alt="RSD Accessories Logo" 
              className="h-16 w-auto mb-5 rounded-lg"
            />
            <p className="text-neutral-400 text-sm mb-5 leading-relaxed">
              RSD A Brand Of New Eon. Premium quality mobile accessories manufactured in India for B2B wholesale.
            </p>
            <div className="flex items-center space-x-3">
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-link"
                className="w-10 h-10 bg-white/10 hover:bg-[#FF4D00] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="facebook-link"
                className="w-10 h-10 bg-white/10 hover:bg-[#FF4D00] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="youtube-link"
                className="w-10 h-10 bg-white/10 hover:bg-[#FF4D00] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-5 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#FF4D00] rounded-full"></span>
              Quick Links
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/shop" data-testid="footer-shop" className="text-neutral-400 hover:text-[#FF4D00] hover:translate-x-1 transition-all duration-200 flex items-center gap-2">
                  <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  Shop All Products
                </Link>
              </li>
              <li>
                <Link to="/warranty-registration" data-testid="footer-warranty" className="text-neutral-400 hover:text-[#FF4D00] hover:translate-x-1 transition-all duration-200 inline-block">
                  Warranty Registration
                </Link>
              </li>
              <li>
                <Link to="/become-distributor" data-testid="footer-distributor" className="text-neutral-400 hover:text-[#FF4D00] hover:translate-x-1 transition-all duration-200 inline-block">
                  Become a Distributor
                </Link>
              </li>
              <li>
                <Link to="/about" data-testid="footer-about" className="text-neutral-400 hover:text-[#FF4D00] hover:translate-x-1 transition-all duration-200 inline-block">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" data-testid="footer-contact" className="text-neutral-400 hover:text-[#FF4D00] hover:translate-x-1 transition-all duration-200 inline-block">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/policies" data-testid="footer-policies" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3 className="font-bold text-lg mb-4">Categories</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/shop/data-cables" data-testid="footer-cables" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                  Data Cables
                </Link>
              </li>
              <li>
                <Link to="/shop/mobile-chargers" data-testid="footer-chargers" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                  Mobile Chargers
                </Link>
              </li>
              <li>
                <Link to="/shop/earbuds" data-testid="footer-earbuds" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                  Earbuds
                </Link>
              </li>
              <li>
                <Link to="/shop/neckbands" data-testid="footer-neckbands" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                  Neckbands
                </Link>
              </li>
            </ul>
            
            {/* Resources */}
            <h3 className="font-bold text-lg mb-4 mt-6">Resources</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/faq" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                  FAQs
                </Link>
              </li>
              <li>
                <Link to="/quality-certifications" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                  Quality & Certifications
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4">Contact Info</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 mt-1 text-[#FF4D00] flex-shrink-0" />
                <span className="text-neutral-300">B-08, Sector-57, Noida-201301 U.P.</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-[#FF4D00] flex-shrink-0" />
                <div className="flex flex-col">
                  <a href="tel:9311211889" data-testid="footer-phone-1" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                    9311211889
                  </a>
                  <a href="tel:9540790030" data-testid="footer-phone-2" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                    9540790030
                  </a>
                  <a href="tel:8595459161" data-testid="footer-phone-3" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                    8595459161
                  </a>
                </div>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-[#FF4D00] flex-shrink-0" />
                <a href="mailto:contact@rsdaccessories.com" data-testid="footer-email" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                  contact@rsdaccessories.com
                </a>
              </li>
            </ul>
            <div className="mt-4">
              <p className="text-xs text-neutral-400 mb-2">Also available on:</p>
              <div className="flex space-x-2">
                <span className="px-3 py-1 bg-neutral-800 rounded text-xs font-medium">Amazon</span>
                <span className="px-3 py-1 bg-neutral-800 rounded text-xs font-medium">Flipkart</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-neutral-800 mt-8 pt-8">
          {/* SEO Authority Pages */}
          <div className="mb-6 p-4 bg-neutral-900/50 rounded-lg border border-neutral-800">
            <h4 className="text-sm font-bold text-white mb-3">Manufacturer Authority Pages</h4>
            <div className="flex flex-wrap gap-2 text-xs">
              <Link to="/best-data-cable-manufacturer-india" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
                Best Data Cable Manufacturer India
              </Link>
              <span className="text-neutral-600">|</span>
              <Link to="/premium-charger-manufacturer-india" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
                Premium Charger Manufacturer
              </Link>
              <span className="text-neutral-600">|</span>
              <Link to="/mobile-accessories-manufacturer-india" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
                Mobile Accessories Manufacturer
              </Link>
              <span className="text-neutral-600">|</span>
              <Link to="/oem-charger-manufacturer-noida" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
                OEM Charger Manufacturer Noida
              </Link>
              <span className="text-neutral-600">|</span>
              <Link to="/fast-charging-cable-manufacturer" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
                Fast Charging Cable Manufacturer
              </Link>
              <span className="text-neutral-600">|</span>
              <Link to="/best-data-cable-factory-in-noida" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
                Data Cable Factory Noida
              </Link>
              <span className="text-neutral-600">|</span>
              <Link to="/best-data-cable-factory-in-delhi" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
                Data Cable Factory Delhi
              </Link>
            </div>
          </div>

          {/* B2B Wholesale Disclaimer */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-900/50 to-blue-800/30 rounded-lg border border-blue-700/50">
            <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-xs">🏭</span>
              B2B Wholesale Disclaimer
            </h4>
            <p className="text-xs text-blue-200 leading-relaxed">
              <strong>IMPORTANT:</strong> RSD Accessories operates exclusively as a B2B (Business-to-Business) wholesale supplier. 
              We do not sell individual units to end consumers. All products are available for bulk orders only with 
              Minimum Order Quantity (MOQ) requirements. Pricing is provided on inquiry basis. For retail purchases, 
              please visit our authorized distributors on Amazon and Flipkart.
            </p>
          </div>

          {/* Legal Metrology Information */}
          <div className="mb-6 p-4 bg-neutral-900 rounded-lg border border-neutral-800">
            <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded bg-[#FF4D00] flex items-center justify-center text-xs">⚖️</span>
              Legal Metrology Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs text-neutral-400">
              <div>
                <p className="text-neutral-500 uppercase text-[10px] tracking-wide mb-1">Manufacturer</p>
                <p className="text-white font-medium">RSD Accessories Pvt Ltd</p>
              </div>
              <div>
                <p className="text-neutral-500 uppercase text-[10px] tracking-wide mb-1">Registered Address</p>
                <p className="text-white font-medium">B-08, Sector-57, Noida-201301, UP, India</p>
              </div>
              <div>
                <p className="text-neutral-500 uppercase text-[10px] tracking-wide mb-1">Customer Care (B2B)</p>
                <p className="text-white font-medium">+91-9311211889</p>
                <p className="text-white font-medium">contact@rsdaccessories.com</p>
              </div>
              <div>
                <p className="text-neutral-500 uppercase text-[10px] tracking-wide mb-1">Country of Origin</p>
                <p className="text-white font-medium">India 🇮🇳</p>
              </div>
            </div>
            <p className="text-[10px] text-neutral-500 mt-3 pt-3 border-t border-neutral-800">
              As per Legal Metrology (Packaged Commodities) Rules 2011.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center mb-4">
            <p className="text-sm text-white mb-4 md:mb-0">
              RSD A Brand Of New Eon. B2B Wholesale Mobile Accessories Manufacturer in India.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy-policy" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/policies" className="text-neutral-300 hover:text-[#FF4D00] transition-colors">
                Terms & Conditions
              </Link>
            </div>
          </div>
          <p className="text-sm text-neutral-400 text-center">
            © 2026 RSD Accessories Pvt Ltd. All rights reserved. | B2B Wholesale Only
          </p>
        </div>

        {/* Made in India Badge */}
        <div className="mt-6 text-center">
          <span className="inline-flex items-center px-4 py-2 bg-neutral-900 rounded-full text-sm font-medium accent-font">
            <span className="text-[#FF4D00] mr-2">🇮🇳</span>
            PROUDLY MADE IN INDIA
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;